import { useState } from 'react';
import { UserPlus } from 'lucide-react';
import type { ProvisionedCredentials } from '@nlinks/shared';
import { ApiError } from '@/lib/api';
import { useAdminActions } from '@/lib/queries';
import { CredentialsDialog } from '@/components/CredentialsDialog';
import { Alert, Button, Card, CardHeader, Field, Input } from '@/components/ui';

/**
 * The admin's create-user screen. Two fields, because that is all the system
 * needs: the email address becomes the login ID and the password is generated.
 */
export function AddUserPage() {
  const { createUser } = useAdminActions();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ credentials: ProvisionedCredentials; fullName: string } | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const response = await createUser.mutateAsync({ fullName: fullName.trim(), email: email.trim() });
      setResult({ credentials: response.credentials, fullName: response.user.fullName });
      setFullName('');
      setEmail('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create this account.');
    }
  };

  return (
    <div className="mx-auto max-w-xl space-y-6">
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-slate-100">Add a user</h1>
        <p className="mt-1 text-sm text-slate-500">
          The system generates a password and shows it once. Send it to them yourself.
        </p>
      </div>

      <Card>
        <CardHeader title="New account" description="Their email address is their login ID." />
        <form onSubmit={submit} className="space-y-5 p-5">
          {error ? <Alert severity="error">{error}</Alert> : null}

          <Field label="Full name" required htmlFor="fullName">
            <Input
              id="fullName"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Priya Sharma"
              required
              minLength={2}
              autoFocus
            />
          </Field>

          <Field
            label="Email address"
            required
            htmlFor="email"
            hint="This is what they will sign in with. It must be an address they control."
          >
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="priya@example.com"
              required
            />
          </Field>

          <div className="flex justify-end border-t border-surface-border pt-5">
            <Button type="submit" loading={createUser.isPending} icon={<UserPlus className="h-4 w-4" />}>
              Create account
            </Button>
          </div>
        </form>
      </Card>

      {result ? (
        <CredentialsDialog
          email={result.credentials.email}
          password={result.credentials.temporaryPassword}
          fullName={result.fullName}
          onClose={() => setResult(null)}
        />
      ) : null}
    </div>
  );
}
